import { useState, useEffect, useMemo, useRef } from 'react';
import { X, Save, Loader2, Download, AlertCircle, CheckCircle, Eye, Code, Clock, RefreshCw, Wand2, HelpCircle, BarChart3, Zap, Check, ImagePlus } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ContentEditor.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import RewriteModal from './RewriteModal';

// Quill cannot natively handle <table> HTML — it strips or mangles table tags.
// Workaround: convert tables to base64-encoded placeholders before loading into Quill,
// and decode them back to real HTML when saving or previewing.
function tablesToPlaceholders(html: string): string {
  return html.replace(/<table[\s\S]*?<\/table>/gi, (match) => {
    const encoded = btoa(unescape(encodeURIComponent(match)));
    return `<div class="ql-table-placeholder" data-table="${encoded}" contenteditable="false">` +
      '<p style="background:#f0f4ff;border:1px dashed #6366f1;border-radius:8px;padding:12px 16px;margin:8px 0;color:#4338ca;font-weight:600;text-align:center;font-size:0.9em;">' +
      '\u{1F4CA} Table (visible in Preview mode)</p></div>';
  });
}

function placeholdersToTables(html: string): string {
  return html.replace(/<div class="ql-table-placeholder"[^>]*data-table="([^"]*)"[^>]*>[\s\S]*?<\/div>/gi, (_match, encoded) => {
    try {
      return decodeURIComponent(escape(atob(encoded)));
    } catch {
      return '';
    }
  });
}

// Quill also strips <figure> and <img> tags — same base64 placeholder approach.
function figuresToPlaceholders(html: string): string {
  return html.replace(/<figure[\s\S]*?<\/figure>/gi, (match) => {
    const encoded = btoa(unescape(encodeURIComponent(match)));
    // Extract alt text or caption for display
    const altMatch = match.match(/alt="([^"]*)"/i);
    const captionMatch = match.match(/<figcaption[^>]*>([\s\S]*?)<\/figcaption>/i);
    const label = captionMatch?.[1]?.replace(/<[^>]+>/g, '').slice(0, 80) || altMatch?.[1]?.slice(0, 80) || 'Infographic';
    return `<div class="ql-figure-placeholder" data-figure="${encoded}" contenteditable="false">` +
      `<p style="background:#f0fdf4;border:1px dashed #22c55e;border-radius:8px;padding:12px 16px;margin:8px 0;color:#15803d;font-weight:600;text-align:center;font-size:0.9em;">` +
      `\u{1F5BC}\u{FE0F} ${label} (visible in Preview mode)</p></div>`;
  });
}

function placeholdersToFigures(html: string): string {
  return html.replace(/<div class="ql-figure-placeholder"[^>]*data-figure="([^"]*)"[^>]*>[\s\S]*?<\/div>/gi, (_match, encoded) => {
    try {
      return decodeURIComponent(escape(atob(encoded)));
    } catch {
      return '';
    }
  });
}

// Register the placeholder divs so Quill doesn't strip them
const QuillAny = Quill as any;
const BlockEmbed = QuillAny.import('blots/block/embed');

class TablePlaceholder extends BlockEmbed {
  static blotName = 'table-placeholder';
  static tagName = 'DIV';
  static className = 'ql-table-placeholder';

  static create(value: string) {
    const node = super.create() as HTMLElement;
    node.setAttribute('data-table', value);
    node.setAttribute('contenteditable', 'false');
    node.innerHTML = '<p style="background:#f0f4ff;border:1px dashed #6366f1;border-radius:8px;padding:12px 16px;margin:8px 0;color:#4338ca;font-weight:600;text-align:center;font-size:0.9em;">\u{1F4CA} Table (visible in Preview mode)</p>';
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('data-table') || '';
  }
}

class FigurePlaceholder extends BlockEmbed {
  static blotName = 'figure-placeholder';
  static tagName = 'DIV';
  static className = 'ql-figure-placeholder';

  static create(value: string) {
    const node = super.create() as HTMLElement;
    node.setAttribute('data-figure', value);
    node.setAttribute('contenteditable', 'false');
    // Decode to get label
    let label = 'Infographic';
    try {
      const decoded = decodeURIComponent(escape(atob(value)));
      const captionMatch = decoded.match(/<figcaption[^>]*>([\s\S]*?)<\/figcaption>/i);
      const altMatch = decoded.match(/alt="([^"]*)"/i);
      label = captionMatch?.[1]?.replace(/<[^>]+>/g, '').slice(0, 80) || altMatch?.[1]?.slice(0, 80) || 'Infographic';
    } catch { /* ignore */ }
    node.innerHTML = `<p style="background:#f0fdf4;border:1px dashed #22c55e;border-radius:8px;padding:12px 16px;margin:8px 0;color:#15803d;font-weight:600;text-align:center;font-size:0.9em;">\u{1F5BC}\u{FE0F} ${label} (visible in Preview mode)</p>`;
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('data-figure') || '';
  }
}

QuillAny.register(TablePlaceholder);
QuillAny.register(FigurePlaceholder);

// HTML to Markdown converter for download
const htmlToMarkdown = (html: string): string => {
  if (!html) return '';

  let markdown = html;

  // Remove style attributes and clean up
  markdown = markdown.replace(/\s*style="[^"]*"/gi, '');
  markdown = markdown.replace(/\s*class="[^"]*"/gi, '');

  // Convert headers
  markdown = markdown.replace(/<h1[^>]*>(.*?)<\/h1>/gi, '# $1\n\n');
  markdown = markdown.replace(/<h2[^>]*>(.*?)<\/h2>/gi, '## $1\n\n');
  markdown = markdown.replace(/<h3[^>]*>(.*?)<\/h3>/gi, '### $1\n\n');
  markdown = markdown.replace(/<h4[^>]*>(.*?)<\/h4>/gi, '#### $1\n\n');
  markdown = markdown.replace(/<h5[^>]*>(.*?)<\/h5>/gi, '##### $1\n\n');
  markdown = markdown.replace(/<h6[^>]*>(.*?)<\/h6>/gi, '###### $1\n\n');

  // Convert bold and italic
  markdown = markdown.replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**');
  markdown = markdown.replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**');
  markdown = markdown.replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*');
  markdown = markdown.replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*');
  markdown = markdown.replace(/<u[^>]*>(.*?)<\/u>/gi, '$1'); // Underline has no markdown equivalent
  markdown = markdown.replace(/<s[^>]*>(.*?)<\/s>/gi, '~~$1~~');
  markdown = markdown.replace(/<strike[^>]*>(.*?)<\/strike>/gi, '~~$1~~');

  // Convert links
  markdown = markdown.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)');

  // Convert inline code
  markdown = markdown.replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`');

  // Convert code blocks
  markdown = markdown.replace(/<pre[^>]*><code[^>]*>([\s\S]*?)<\/code><\/pre>/gi, '```\n$1\n```\n\n');
  markdown = markdown.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, '```\n$1\n```\n\n');

  // Convert blockquotes
  markdown = markdown.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (match, content) => {
    const lines = content.replace(/<[^>]+>/g, '').split('\n');
    return lines.map((line: string) => `> ${line.trim()}`).join('\n') + '\n\n';
  });

  // Convert unordered lists
  markdown = markdown.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, content) => {
    const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    return items.map((item: string) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim();
      return `- ${text}`;
    }).join('\n') + '\n\n';
  });

  // Convert ordered lists
  markdown = markdown.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, content) => {
    const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    return items.map((item: string, index: number) => {
      const text = item.replace(/<\/?li[^>]*>/gi, '').replace(/<[^>]+>/g, '').trim();
      return `${index + 1}. ${text}`;
    }).join('\n') + '\n\n';
  });

  // Convert horizontal rules
  markdown = markdown.replace(/<hr[^>]*\/?>/gi, '\n---\n\n');

  // Convert paragraphs
  markdown = markdown.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '$1\n\n');

  // Convert line breaks
  markdown = markdown.replace(/<br[^>]*\/?>/gi, '\n');

  // Remove remaining HTML tags
  markdown = markdown.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  markdown = markdown.replace(/&amp;/g, '&');
  markdown = markdown.replace(/&lt;/g, '<');
  markdown = markdown.replace(/&gt;/g, '>');
  markdown = markdown.replace(/&nbsp;/g, ' ');
  markdown = markdown.replace(/&quot;/g, '"');
  markdown = markdown.replace(/&#39;/g, "'");
  markdown = markdown.replace(/&rsquo;/g, "'");
  markdown = markdown.replace(/&lsquo;/g, "'");
  markdown = markdown.replace(/&rdquo;/g, '"');
  markdown = markdown.replace(/&ldquo;/g, '"');
  markdown = markdown.replace(/&mdash;/g, '—');
  markdown = markdown.replace(/&ndash;/g, '–');
  markdown = markdown.replace(/&hellip;/g, '...');

  // Clean up excessive whitespace
  markdown = markdown.replace(/\n{3,}/g, '\n\n');
  markdown = markdown.trim();

  return markdown;
};

// Improved markdown to HTML converter
const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';

  let html = markdown;

  // Remove QA markers and similar artifacts
  html = html.replace(/^QA:?\s*$/gm, '');
  html = html.replace(/^\[QA\].*$/gm, '');
  html = html.replace(/^Q&A:?\s*$/gm, '');

  // Escape HTML special characters (but preserve line structure)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Process line by line for better control
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  let listType = '';

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Skip empty lines (will be handled in paragraph grouping)
    if (line.trim() === '') {
      if (inList) {
        processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
        listType = '';
      }
      processedLines.push('');
      continue;
    }

    // Headers - must start with # followed by space and have content
    const h6Match = line.match(/^######\s+(.+)$/);
    const h5Match = line.match(/^#####\s+(.+)$/);
    const h4Match = line.match(/^####\s+(.+)$/);
    const h3Match = line.match(/^###\s+(.+)$/);
    const h2Match = line.match(/^##\s+(.+)$/);
    const h1Match = line.match(/^#\s+(.+)$/);

    if (h6Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h6>${h6Match[1].trim()}</h6>`);
      continue;
    }
    if (h5Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h5>${h5Match[1].trim()}</h5>`);
      continue;
    }
    if (h4Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h4>${h4Match[1].trim()}</h4>`);
      continue;
    }
    if (h3Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h3>${h3Match[1].trim()}</h3>`);
      continue;
    }
    if (h2Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h2>${h2Match[1].trim()}</h2>`);
      continue;
    }
    if (h1Match) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<h1>${h1Match[1].trim()}</h1>`);
      continue;
    }

    // Horizontal rules
    if (/^[-*_]{3,}\s*$/.test(line)) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push('<hr>');
      continue;
    }

    // Unordered list items - must have content after the marker
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (ulMatch && ulMatch[2].trim()) {
      if (!inList || listType !== 'ul') {
        if (inList) processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        processedLines.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      processedLines.push(`<li>${ulMatch[2].trim()}</li>`);
      continue;
    }

    // Ordered list items - must have content after the number
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (olMatch && olMatch[3].trim()) {
      if (!inList || listType !== 'ol') {
        if (inList) processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        processedLines.push('<ol>');
        inList = true;
        listType = 'ol';
      }
      processedLines.push(`<li>${olMatch[3].trim()}</li>`);
      continue;
    }

    // Blockquotes
    const bqMatch = line.match(/^>\s*(.*)$/);
    if (bqMatch) {
      if (inList) { processedLines.push(listType === 'ul' ? '</ul>' : '</ol>'); inList = false; }
      processedLines.push(`<blockquote>${bqMatch[1].trim()}</blockquote>`);
      continue;
    }

    // Close list if we hit a regular line
    if (inList) {
      processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
      inList = false;
      listType = '';
    }

    // Regular text line
    processedLines.push(line);
  }

  // Close any open list
  if (inList) {
    processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
  }

  html = processedLines.join('\n');

  // Merge consecutive blockquotes
  html = html.replace(/<\/blockquote>\n<blockquote>/g, '<br>');

  // Bold and italic (inline formatting)
  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  html = html.replace(/___(.+?)___/g, '<strong><em>$1</em></strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Group non-block lines into paragraphs
  const finalLines = html.split('\n');
  const result: string[] = [];
  let paragraphLines: string[] = [];

  const isBlockElement = (line: string) => {
    return line.startsWith('<h') || line.startsWith('<ul') || line.startsWith('</ul') ||
           line.startsWith('<ol') || line.startsWith('</ol') || line.startsWith('<li') ||
           line.startsWith('<blockquote') || line.startsWith('<pre') ||
           line.startsWith('<hr') || line.trim() === '';
  };

  for (const line of finalLines) {
    if (isBlockElement(line)) {
      // Flush paragraph buffer
      if (paragraphLines.length > 0) {
        const pContent = paragraphLines.join('<br>').trim();
        if (pContent) {
          result.push(`<p>${pContent}</p>`);
        }
        paragraphLines = [];
      }
      if (line.trim()) {
        result.push(line);
      }
    } else {
      paragraphLines.push(line);
    }
  }

  // Flush remaining paragraph
  if (paragraphLines.length > 0) {
    const pContent = paragraphLines.join('<br>').trim();
    if (pContent) {
      result.push(`<p>${pContent}</p>`);
    }
  }

  // Clean up empty paragraphs and list items
  html = result.join('\n');
  html = html.replace(/<p>\s*<\/p>/g, '');
  html = html.replace(/<li>\s*<\/li>/g, '');
  html = html.replace(/<p><br><\/p>/g, '');
  html = html.replace(/<p>(<br>)+<\/p>/g, '');

  // Remove empty lists
  html = html.replace(/<ul>\s*<\/ul>/g, '');
  html = html.replace(/<ol>\s*<\/ol>/g, '');

  return html;
};

interface ContentEditorModalProps {
  // Original props for FactsAgent
  jobId?: number;
  stepNumber?: number;
  stepName?: string;
  // Generic props for other content types
  contentType?: string;
  contentId?: number;
  contentTitle?: string;
  apiEndpoint?: string;
  // Common props
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
  onRewrite?: () => void;
}

export default function ContentEditorModal({
  jobId,
  stepNumber,
  stepName,
  contentType,
  contentId,
  contentTitle,
  apiEndpoint,
  isOpen,
  onClose,
  onSave,
  onRewrite
}: ContentEditorModalProps) {
  const { user } = useAuth();
  const quillRef = useRef<ReactQuill>(null);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [editedAt, setEditedAt] = useState<string | null>(null);
  const [editedByName, setEditedByName] = useState<string | null>(null);
  const [showRewriteModal, setShowRewriteModal] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [h1Title, setH1Title] = useState('');
  const [originalH1Title, setOriginalH1Title] = useState('');
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqKeyword, setFaqKeyword] = useState('');
  const [faqSubKeyword, setFaqSubKeyword] = useState('');
  const [faqResults, setFaqResults] = useState<{question: string; answer: string}[]>([]);
  const [faqHtml, setFaqHtml] = useState('');
  const [faqSelectedIndexes, setFaqSelectedIndexes] = useState<Set<number>>(new Set());
  // Competitive Analysis state
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState<any>(null);
  const [applyingIndex, setApplyingIndex] = useState<number | null>(null);
  const [appliedIndexes, setAppliedIndexes] = useState<Set<number>>(new Set());
  // Image generation state
  const [generatingImages, setGeneratingImages] = useState(false);
  const [imagesStatus, setImagesStatus] = useState<string | null>(null);
  const imagesPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Rich text editor configuration (WordPress-style)
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'blockquote', 'code-block'],
      ['clean']
    ],
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'color', 'background',
    'align',
    'link', 'blockquote', 'code-block',
    'image',
    'table-placeholder',
    'figure-placeholder',
  ];

  useEffect(() => {
    if (isOpen) {
      loadContent();
    }
  }, [isOpen, jobId, stepNumber, apiEndpoint, contentId]);

  useEffect(() => {
    // Update word count (strip HTML tags first)
    const stripHtml = (html: string) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    const textOnly = stripHtml(content);
    const words = textOnly.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount(words.length);

    // Check if content has changed
    setHasChanges(content !== originalContent || h1Title !== originalH1Title);
  }, [content, originalContent]);

  const loadContent = async () => {
    try {
      setLoading(true);
      setError('');

      // Use generic apiEndpoint if provided, otherwise use FactsAgent endpoint
      if (apiEndpoint) {
        // Generic content loading (Service Area Pages, etc.)
        const resp = await api.get(apiEndpoint);
        const data = resp.data;

        if (data && data.content !== undefined) {
          const rawContent = data.content || '';
          let htmlContent = rawContent;

          // If content starts with markdown indicators, convert it
          const hasHtmlTags = /<[a-z][\s\S]*>/i.test(rawContent);
          const startsWithMarkdown = /^#|^\*|^-\s|^\d+\.\s/m.test(rawContent.trim());

          if (!hasHtmlTags || startsWithMarkdown) {
            htmlContent = markdownToHtml(rawContent);
          }

          // Extract H1 and strip it from editor content
          const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
          if (h1Match) {
            const extractedH1 = h1Match[1].replace(/<[^>]+>/g, '');
            setH1Title(extractedH1);
            setOriginalH1Title(extractedH1);
            htmlContent = htmlContent.replace(/<h1[^>]*>.*?<\/h1>/i, '');
          } else {
            setH1Title('');
            setOriginalH1Title('');
          }

          setContent(figuresToPlaceholders(tablesToPlaceholders(htmlContent)));
          setOriginalContent(htmlContent);

          // Set edit tracking info from response
          setEditedAt(data.edited_at || null);
          setEditedByName(data.edited_by_name || null);
          setGeneratedAt(data.generated_at || null);
        } else {
          throw new Error('Content not found');
        }
      } else {
        // Original FactsAgent endpoint
        const response = await api.get(`/factsheets/${jobId}/all-steps`);
        const data = response.data;
        const step = data.steps.find((s: any) => s.step_number === stepNumber);

        if (step && step.content) {
          const rawContent = step.content;
          let htmlContent = rawContent;

          const hasHtmlTags = /<[a-z][\s\S]*>/i.test(rawContent);
          const startsWithMarkdown = /^#|^\*|^-\s|^\d+\.\s/m.test(rawContent.trim());

          if (!hasHtmlTags || startsWithMarkdown) {
            htmlContent = markdownToHtml(rawContent);
          }

          // Extract H1 and strip it from editor content
          const h1Match = htmlContent.match(/<h1[^>]*>(.*?)<\/h1>/i);
          if (h1Match) {
            const extractedH1 = h1Match[1].replace(/<[^>]+>/g, '');
            setH1Title(extractedH1);
            setOriginalH1Title(extractedH1);
            htmlContent = htmlContent.replace(/<h1[^>]*>.*?<\/h1>/i, '');
          } else {
            setH1Title('');
            setOriginalH1Title('');
          }

          setContent(figuresToPlaceholders(tablesToPlaceholders(htmlContent)));
          setOriginalContent(htmlContent);

          setEditedAt(step.edited_at || null);
          setEditedByName(step.edited_by_name || null);
          setGeneratedAt(step.generated_at || null);
        } else {
          throw new Error('Step content not found');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSaveSuccess(false);

      const editorName = user?.name || user?.email || 'Unknown';
      // Convert table placeholders back to real HTML tables, then prepend H1
      const restoredContent = placeholdersToFigures(placeholdersToTables(content));
      const fullContent = h1Title ? `<h1>${h1Title}</h1>${restoredContent}` : restoredContent;

      let result: any;
      if (apiEndpoint) {
        // Generic save endpoint (Service Area Pages, etc.)
        const formData = new FormData();
        formData.append('content', fullContent);
        formData.append('edited_by_name', editorName);
        const resp = await api.put(apiEndpoint, formData);
        result = resp.data;
      } else {
        // Original FactsAgent endpoint
        const resp = await api.put(`/factsheets/${jobId}/step/${stepNumber}`, {
          content: fullContent, edited_by_name: editorName,
        });
        result = resp.data;
      }
      setOriginalContent(content);
      setHasChanges(false);
      setSaveSuccess(true);

      // Update edit tracking info
      setEditedAt(result.edited_at || new Date().toISOString());
      setEditedByName(result.edited_by_name || editorName);

      // Show success message for 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);

      if (onSave) {
        onSave();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    // Convert HTML content to Markdown for download (include H1, restore tables)
    const restoredContent = placeholdersToFigures(placeholdersToTables(content));
    const fullContent = h1Title ? `<h1>${h1Title}</h1>${restoredContent}` : restoredContent;
    const markdownContent = htmlToMarkdown(fullContent);
    const blob = new Blob([markdownContent], { type: 'text/markdown; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Use contentTitle for generic content, or stepName for FactsAgent
    const filename = contentTitle
      ? `${contentTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_')}.md`
      : `step${stepNumber}_${(stepName || 'content').toLowerCase().replace(/\s+/g, '_')}.md`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    if (hasChanges) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleOpenRewrite = () => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection();
    if (selection && selection.length > 0) {
      const text = editor.getText(selection.index, selection.length);
      setSelectedText(text);
      setShowRewriteModal(true);
    } else {
      // No selection — use all plain text
      const allText = editor.getText();
      if (allText.trim()) {
        setSelectedText(allText.trim());
        setShowRewriteModal(true);
      }
    }
  };

  const handleRewriteAccept = (rewrittenText: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const selection = editor.getSelection();
    if (selection && selection.length > 0) {
      editor.deleteText(selection.index, selection.length);
      editor.insertText(selection.index, rewrittenText);
    } else {
      // Replace entire content
      const htmlContent = `<p>${rewrittenText.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
      setContent(htmlContent);
    }
    setShowRewriteModal(false);
    setSelectedText('');
  };

  const handleFaqFetch = async () => {
    if (!faqKeyword.trim()) return;
    setFaqLoading(true);
    setFaqResults([]);
    setFaqHtml('');
    setFaqSelectedIndexes(new Set());
    try {
      const resp = await fetch('/api/content-writer/fetch-faqs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: faqKeyword.trim(), sub_keyword: faqSubKeyword.trim() }),
      });
      if (!resp.ok) throw new Error('Failed to fetch FAQs');
      const data = await resp.json();
      setFaqResults(data.faqs || []);
      setFaqHtml(data.faq_html || '');
      // Select all by default
      setFaqSelectedIndexes(new Set((data.faqs || []).map((_: any, i: number) => i)));
    } catch (err) {
      console.error('FAQ fetch error:', err);
    } finally {
      setFaqLoading(false);
    }
  };

  const handleFaqToggle = (index: number) => {
    setFaqSelectedIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleFaqAppend = () => {
    const selectedFaqs = faqResults.filter((_, i) => faqSelectedIndexes.has(i));
    if (selectedFaqs.length === 0) return;

    let html = '<h2>Frequently Asked Questions</h2>\n';
    for (const faq of selectedFaqs) {
      const q = faq.question.endsWith('?') ? faq.question : faq.question + '?';
      html += `<h3>${q}</h3>\n`;
      if (faq.answer) html += `<p>${faq.answer}</p>\n`;
    }

    setContent(prev => prev + '\n' + html);
    setShowFaqModal(false);
    setFaqResults([]);
    setFaqHtml('');
  };

  const handleAnalyze = async () => {
    const kw = h1Title || contentTitle || '';
    if (!kw) return;
    setAnalysisLoading(true);
    setAnalysisData(null);
    setAppliedIndexes(new Set());
    try {
      const fullContent = h1Title ? `<h1>${h1Title}</h1>${placeholdersToFigures(placeholdersToTables(content))}` : placeholdersToFigures(placeholdersToTables(content));
      const resp = await fetch('/api/content-writer/analyze-competitors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: kw, content_html: fullContent }),
      });
      if (!resp.ok) throw new Error('Analysis failed');
      const data = await resp.json();
      setAnalysisData(data);
    } catch (err) {
      console.error('Analysis error:', err);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleApplyImprovement = async (index: number, instruction: string) => {
    setApplyingIndex(index);
    try {
      const fullContent = h1Title ? `<h1>${h1Title}</h1>${placeholdersToFigures(placeholdersToTables(content))}` : placeholdersToFigures(placeholdersToTables(content));
      const kw = h1Title || contentTitle || '';
      const resp = await fetch('/api/content-writer/apply-improvement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: kw, content_html: fullContent, improvement: instruction }),
      });
      if (!resp.ok) throw new Error('Failed to apply improvement');
      const data = await resp.json();
      if (data.status === 'success' && data.content_html) {
        // Extract H1 from improved content
        const h1Match = data.content_html.match(/<h1[^>]*>(.*?)<\/h1>/i);
        if (h1Match) {
          setH1Title(h1Match[1].replace(/<[^>]+>/g, ''));
          setContent(figuresToPlaceholders(tablesToPlaceholders(data.content_html.replace(/<h1[^>]*>.*?<\/h1>/i, ''))));
        } else {
          setContent(figuresToPlaceholders(tablesToPlaceholders(data.content_html)));
        }
        setAppliedIndexes(prev => new Set([...prev, index]));
      }
    } catch (err) {
      console.error('Apply improvement error:', err);
    } finally {
      setApplyingIndex(null);
    }
  };

  const handleGenerateImages = async () => {
    if (!contentId) return;
    const customPrompt = imagesStatus === 'completed'
      ? (window.prompt('Custom instructions for regeneration (leave empty for default):') ?? '')
      : '';
    setGeneratingImages(true);
    setImagesStatus('generating');
    try {
      await api.post(`/content-writer/articles/${contentId}/generate-images`, { custom_prompt: customPrompt });
      // Poll for completion
      if (imagesPollRef.current) clearInterval(imagesPollRef.current);
      imagesPollRef.current = setInterval(async () => {
        try {
          const res = await api.get(`/content-writer/articles/${contentId}/images`);
          const data = res.data;
          setImagesStatus(data.images_status);
          if (data.images_status === 'completed' || data.images_status === 'failed') {
            if (imagesPollRef.current) clearInterval(imagesPollRef.current);
            setGeneratingImages(false);
            // Reload the content to get injected infographics
            if (data.images_status === 'completed') {
              const contentRes = await api.get(`/content-writer/articles/${contentId}/content`);
              const contentData = contentRes.data;
              if (contentData.content) {
                const h1Match = contentData.content.match(/<h1[^>]*>(.*?)<\/h1>/i);
                if (h1Match) {
                  setContent(figuresToPlaceholders(tablesToPlaceholders(contentData.content.replace(/<h1[^>]*>.*?<\/h1>/i, ''))));
                } else {
                  setContent(figuresToPlaceholders(tablesToPlaceholders(contentData.content)));
                }
              }
            }
          }
        } catch { /* ignore polling errors */ }
      }, 5000);
    } catch (err) {
      console.error('Image generation error:', err);
      setGeneratingImages(false);
      setImagesStatus('failed');
    }
  };

  // Cleanup image poll on unmount
  useEffect(() => {
    return () => { if (imagesPollRef.current) clearInterval(imagesPollRef.current); };
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {h1Title ? `Edit: ${h1Title}` : contentTitle ? `Edit: ${contentTitle}` : `Edit Step ${stepNumber}: ${stepName}`}
              </h2>
              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                <span>{wordCount.toLocaleString()} words</span>
                {editedAt && editedByName ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last edited {new Date(editedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} by <span className="font-medium text-gray-600">{editedByName}</span>
                  </span>
                ) : generatedAt ? (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Generated {new Date(generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                ) : null}
                {hasChanges && <span className="text-orange-600">• Unsaved changes</span>}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* View Mode Toggle */}
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('edit')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'edit'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Edit Mode"
                >
                  <Code className="h-4 w-4 inline mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'preview'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Preview Mode"
                >
                  <Eye className="h-4 w-4 inline mr-1" />
                  Preview
                </button>
              </div>

              <button
                onClick={handleOpenRewrite}
                className="px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors flex items-center gap-1.5"
                title="AI Rewrite — select text first, or rewrites entire content"
              >
                <Wand2 className="h-4 w-4" />
                AI Rewrite
              </button>
              <button
                onClick={() => {
                  setFaqKeyword(h1Title || contentTitle || '');
                  setFaqSubKeyword('');
                  setFaqResults([]);
                  setFaqHtml('');
                  setShowFaqModal(true);
                }}
                className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1.5"
                title="Fetch FAQ questions from Google and generate answers"
              >
                <HelpCircle className="h-4 w-4" />
                FAQ Fetch
              </button>
              <button
                onClick={() => {
                  setShowAnalysisModal(true);
                  if (!analysisData) handleAnalyze();
                }}
                className="px-3 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-1.5"
                title="Analyze article against top 10 competitors"
              >
                <BarChart3 className="h-4 w-4" />
                Analyze
              </button>
              {contentId && (
                <button
                  onClick={handleGenerateImages}
                  disabled={generatingImages}
                  className="px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  title="Generate thumbnail + infographic images for this article"
                >
                  {generatingImages ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  {imagesStatus === 'completed' ? 'Regen Images' : generatingImages ? 'Generating...' : 'Gen Images'}
                </button>
              )}
              <button
                onClick={handleDownload}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="btn-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                <span className="ml-3 text-gray-600">Loading content...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <p className="text-red-700 font-medium">{error}</p>
                  <button
                    onClick={loadContent}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : viewMode === 'edit' ? (
              <div className="h-full flex flex-col">
                {/* Editable H1 above editor */}
                {h1Title !== undefined && (
                  <div className="mb-3">
                    <input
                      type="text"
                      value={h1Title}
                      onChange={(e) => setH1Title(e.target.value)}
                      placeholder="Article title (H1)..."
                      className="w-full text-2xl font-bold text-gray-900 border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
                <div className="flex-1 min-h-0">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Start writing your content here... Use the toolbar above to format your text."
                    className="h-full"
                    style={{ height: '100%' }}
                  />
                </div>
              </div>
            ) : (
              <div className="h-full overflow-auto">
                <style>{`
                  .article-content table { width: 100%; border-collapse: collapse; margin: 1.5em 0; }
                  .article-content th, .article-content td { border: 1px solid #d1d5db; padding: 10px 14px; text-align: left; }
                  .article-content th { background-color: #f3f4f6; font-weight: 600; }
                  .article-content tr:nth-child(even) { background-color: #f9fafb; }
                  .article-content .callout { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 14px 18px; margin: 1.5em 0; border-radius: 6px; }
                  .article-content blockquote { border-left: 4px solid #6366f1; background: #eef2ff; padding: 14px 18px; margin: 1.5em 0; border-radius: 4px; }
                  .article-content h1 { font-size: 2rem; font-weight: 700; color: #111827; margin: 0 0 1.5rem 0; }
                  .article-content h2 { font-size: 1.5rem; font-weight: 600; color: #1f2937; margin: 2rem 0 1rem 0; }
                  .article-content h3 { font-size: 1.2rem; font-weight: 600; color: #374151; margin: 1.5rem 0 0.75rem 0; }
                  .article-content p { margin: 0 0 1rem 0; line-height: 1.75; color: #374151; }
                  .article-content ul, .article-content ol { margin: 1rem 0 1.5rem 1.5rem; padding-left: 1rem; }
                  .article-content li { margin-bottom: 0.5rem; line-height: 1.6; }
                  .article-content strong { color: #111827; }
                  .article-content img { max-width: 100%; height: auto; border-radius: 8px; margin: 1rem 0; }
                  .article-content figure { margin: 1.5rem 0; text-align: center; }
                  .article-content figcaption { font-size: 0.875rem; color: #6b7280; margin-top: 0.5rem; }
                `}</style>
                {/* Show H1 separately above content */}
                <div className="bg-white rounded-lg border border-gray-200">
                  {h1Title && (
                    <div className="px-8 pt-8 pb-4">
                      <h1 className="text-3xl font-bold text-gray-900 leading-tight">{h1Title}</h1>
                    </div>
                  )}
                  <div className="prose prose-lg max-w-none p-8 document-preview article-content">
                    <div dangerouslySetInnerHTML={{ __html: placeholdersToFigures(placeholdersToTables(content)) }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span>Word count: {wordCount.toLocaleString()}</span>
                <span className="mx-3">•</span>
                <span>Characters: {content.length.toLocaleString()}</span>
              </div>

              {saveSuccess && (
                <div className="flex items-center text-green-700">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">Saved successfully!</span>
                </div>
              )}

              {error && !loading && (
                <div className="flex items-center text-red-700">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <RewriteModal
        isOpen={showRewriteModal}
        selectedText={selectedText}
        onClose={() => { setShowRewriteModal(false); setSelectedText(''); }}
        onAccept={handleRewriteAccept}
      />

      {/* FAQ Fetch Modal */}
      {showFaqModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowFaqModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">FAQ Fetch</h3>
              <button onClick={() => setShowFaqModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4 overflow-auto flex-1">
              {/* Keyword inputs */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Main Keyword</label>
                  <input
                    type="text"
                    value={faqKeyword}
                    onChange={(e) => setFaqKeyword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. Fentanyl Addiction"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub Keyword (optional)</label>
                  <input
                    type="text"
                    value={faqSubKeyword}
                    onChange={(e) => setFaqSubKeyword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. treatment options"
                  />
                </div>
              </div>

              <button
                onClick={handleFaqFetch}
                disabled={faqLoading || !faqKeyword.trim()}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2"
              >
                {faqLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Fetching FAQs from Google...
                  </>
                ) : (
                  <>
                    <HelpCircle className="h-4 w-4" />
                    Fetch Top 10 FAQs
                  </>
                )}
              </button>

              {/* Results */}
              {faqResults.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">{faqResults.length} FAQs found — select which to append:</p>
                    <button
                      onClick={() => {
                        if (faqSelectedIndexes.size === faqResults.length) {
                          setFaqSelectedIndexes(new Set());
                        } else {
                          setFaqSelectedIndexes(new Set(faqResults.map((_, i) => i)));
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      {faqSelectedIndexes.size === faqResults.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-auto">
                    {faqResults.map((faq, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          faqSelectedIndexes.has(i)
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => handleFaqToggle(i)}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={faqSelectedIndexes.has(i)}
                            onChange={() => handleFaqToggle(i)}
                            className="mt-1 h-4 w-4 text-blue-600 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900">{faq.question}</p>
                            {faq.answer && (
                              <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Append button */}
            {faqResults.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                <button
                  onClick={handleFaqAppend}
                  disabled={faqSelectedIndexes.size === 0}
                  className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Append {faqSelectedIndexes.size} Selected FAQ{faqSelectedIndexes.size !== 1 ? 's' : ''} to Article
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Competitive Analysis Modal */}
      {showAnalysisModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAnalysisModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Competitive Analysis</h3>
                <p className="text-sm text-gray-500 mt-0.5">Analyzing against top 10 competitors for: {h1Title || contentTitle}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleAnalyze}
                  disabled={analysisLoading}
                  className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-1"
                >
                  <RefreshCw className={`h-3 w-3 ${analysisLoading ? 'animate-spin' : ''}`} />
                  Re-analyze
                </button>
                <button onClick={() => setShowAnalysisModal(false)} className="p-1 text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {analysisLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mb-4" />
                  <p className="text-gray-600 font-medium">Analyzing competitors...</p>
                  <p className="text-sm text-gray-400 mt-1">Fetching top 10 results, scraping content, running analysis</p>
                </div>
              ) : analysisData?.error ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                  <p className="text-red-700">{analysisData.error}</p>
                </div>
              ) : analysisData ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-700 leading-relaxed">{analysisData.summary}</p>
                  </div>

                  {/* Strengths */}
                  {analysisData.strengths?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-1.5">
                        <CheckCircle className="h-4 w-4" /> Your Strengths
                      </h4>
                      <div className="space-y-1">
                        {analysisData.strengths.map((s: string, i: number) => (
                          <p key={i} className="text-sm text-gray-600 pl-6">+ {s}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Improvements */}
                  {analysisData.improvements?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Improvements to Apply</h4>
                      <div className="space-y-3">
                        {analysisData.improvements.map((imp: any, i: number) => (
                          <div
                            key={i}
                            className={`rounded-lg border p-4 ${
                              appliedIndexes.has(i)
                                ? 'border-green-200 bg-green-50'
                                : imp.priority === 'critical'
                                ? 'border-red-200 bg-red-50/30'
                                : imp.priority === 'high'
                                ? 'border-orange-200 bg-orange-50/30'
                                : 'border-gray-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                    imp.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                    imp.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {imp.priority}
                                  </span>
                                  <span className="text-xs text-gray-400">{imp.category?.replace('_', ' ')}</span>
                                </div>
                                <p className="text-sm font-semibold text-gray-900">{imp.title}</p>
                                <p className="text-sm text-gray-600 mt-1">{imp.description}</p>
                              </div>
                              <button
                                onClick={() => handleApplyImprovement(i, imp.instruction)}
                                disabled={applyingIndex !== null || appliedIndexes.has(i)}
                                className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 ${
                                  appliedIndexes.has(i)
                                    ? 'bg-green-100 text-green-700 cursor-default'
                                    : applyingIndex === i
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                                }`}
                              >
                                {appliedIndexes.has(i) ? (
                                  <><Check className="h-3 w-3" /> Applied</>
                                ) : applyingIndex === i ? (
                                  <><Loader2 className="h-3 w-3 animate-spin" /> Applying...</>
                                ) : (
                                  <><Zap className="h-3 w-3" /> Apply</>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Competitor Highlights */}
                  {analysisData.competitor_highlights?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Competitor Highlights</h4>
                      <div className="space-y-2">
                        {analysisData.competitor_highlights.map((ch: any, i: number) => (
                          <div key={i} className="text-sm flex gap-2">
                            <span className="font-medium text-blue-600 flex-shrink-0">{ch.domain}:</span>
                            <span className="text-gray-600">{ch.strength}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Competitors list */}
                  {analysisData.competitors?.length > 0 && (
                    <details className="text-sm">
                      <summary className="cursor-pointer font-medium text-gray-500 hover:text-gray-700">
                        View {analysisData.competitors.length} competitors analyzed
                      </summary>
                      <div className="mt-2 space-y-1 pl-4">
                        {analysisData.competitors.map((c: any, i: number) => (
                          <div key={i} className="text-xs text-gray-500">
                            {i+1}. <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{c.domain}</a> — {c.title}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
